import type { ReactNode } from 'react';
import { sectionForTab, tabFor } from '@player/domain';
import { PhoneShell, Shell, type ShellProps } from '@player/ui';
import { usePhoneLayout } from './usePhoneLayout.js';

/**
 * La cornice dell'applicazione: colonna laterale sul televisore, barra di schede sul telefono.
 *
 * ## Perché un involucro e non due rami in `LiveHost`
 *
 * Perché il **contenuto è lo stesso**: duecento righe che scelgono la schermata secondo la sezione.
 * Duplicandole per il telefono si otterrebbero due alberi da tenere d'accordo per sempre, e il modo
 * in cui divergono è silenzioso — una schermata aggiunta a uno solo dei due, che su un apparecchio
 * c'è e sull'altro no.
 *
 * Qui cambia **solo la cornice**, e i figli passano identici.
 *
 * ## Cosa questo involucro non fa ancora, e va detto
 *
 * Mette la barra di schede al posto della colonna, cioè toglie i 280 px di menu e dà una navigazione
 * da pollice. **Non sostituisce ancora le schermate**: dentro ci sono quelle costruite per il
 * televisore, che si usano col dito perché tutto è cliccabile ma sono disegnate per un telecomando.
 *
 * Le schermate del telefono esistono — `PhoneHomeScreen`, `PhoneGuideScreen`, `PhoneVodScreen`,
 * `PhoneSearchScreen`, `PhoneSettingsScreen` — e vanno innestate una per volta dentro `LiveHost`,
 * dove stanno i dati che vogliono. Innestarle tutte insieme qui vorrebbe dire rifare la selezione
 * delle schermate una seconda volta, cioè esattamente i due alberi che questo involucro esiste per
 * non creare.
 *
 * È un passo dichiarato e non una mezza misura nascosta: chi apre l'app su un telefono vede una
 * navigazione giusta e schermate ancora da adattare, non un menu laterale che si mangia due terzi
 * dello schermo.
 */
export function AppChrome({
  section,
  onPickSection,
  hideChrome = false,
  children,
  ...shell
}: Omit<ShellProps, 'children' | 'activeId'> & {
  /** La sezione corrente, con i nomi del televisore: `home`, `live`, `movies`, … */
  readonly section: string;
  /** Dove andare toccando una scheda. Riceve i nomi del televisore, non quelli delle schede. */
  readonly onPickSection: (section: string) => void;
  /**
   * Toglie marchio e schede, lasciando allo schermo solo il contenuto.
   *
   * Vale solo sul telefono: sul televisore la colonna si nasconde già da sé quando qualcosa va a
   * schermo pieno, con una regola sua (`menuVisible`), e due regole per la stessa cosa divergono
   * alla prima che qualcuno ritocca.
   */
  readonly hideChrome?: boolean;
  readonly children: ReactNode;
}) {
  const phone = usePhoneLayout();

  if (!phone) {
    return (
      <Shell {...shell} activeId={section}>
        {children}
      </Shell>
    );
  }

  return (
    <PhoneShell
      /*
        Quale scheda accendere lo decide il dominio, e può rispondere `null`: è una sezione che
        nessuno ha mappato sul telefono. La barra resta visibile tutta spenta — si nota subito,
        mentre una scheda accesa a caso no.
      */
      activeTab={tabFor({ kind: 'section', section })}
      onPickTab={(tab) => onPickSection(sectionForTab(tab))}
      profileName={shell.profileName ?? null}
      onSignOut={shell.onSignOut}
      hideChrome={hideChrome}
    >
      {children}
    </PhoneShell>
  );
}
